import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listWhatsappThreads } from '../../services/whatsappApi';
import WhatsAppThreadPanel from './WhatsAppThreadPanel';
import DeptTag from './DeptTag';
import {
  ChatBubbleLeftRightIcon,
  NoSymbolIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const LIST_POLL_MS = 12000;

// WA-DEPARTMENT-CONTEXT-SPLIT-aug25: a phone can have TWO conversations
// (ventas + postventa). A thread's identity is (lead_phone, department), NOT
// lead_phone alone — using the phone as a React key would collide the two rows
// and break selection. normalizeDept keeps legacy rows (no field) as ventas.
function normalizeDept(d) {
  const s = (d || '').trim().toLowerCase();
  return s === 'postventa' ? 'postventa' : 'ventas';
}

// Stable identity for a thread row / selection.
function threadKey(th) {
  return `${th.lead_phone || ''}|${normalizeDept(th.department)}`;
}

// Canonicalize a phone to its 10-digit national significant number so the
// Calendar cita deep-link (?phone=<digits>) matches a thread's lead_phone across
// MX format variants: +52 vs +521 (mobile '1'), country-code presence/absence,
// and a '+' that decoded to a space in the query string. Mirrors the matcher
// used by PostventaPage/CampaignDetail/CallHistory for the same deep-link.
function normMxPhone(p) {
  const digits = String(p || '').replace(/[^0-9]/g, '');
  if (!digits) return '';
  // Drop the Mexican mobile '1' between the 52 country code and the 10-digit
  // number (52 1 XXXXXXXXXX -> 52 XXXXXXXXXX) before taking the tail.
  const deMobile = digits.replace(/^521(\d{10})$/, '52$1');
  return deMobile.length > 10 ? deMobile.slice(-10) : deMobile;
}

// WA-INBOX-SEARCH-oct19 (Roger promised José a search box on the Oct 19 call):
// one text box filters the chat list by customer NAME or PHONE, partial match.
//  - Name: accent/case-insensitive substring ("jose" finds "José Pérez").
//  - Phone: the query is reduced to its digits and matched as a substring of the
//    thread's phone digits, so "55 1234", "(55) 1234" and "+52 55 1234" all find
//    "+5215512345678". The stored number may carry the MX mobile '1' after the
//    52 country code (+521…), while people type "+52 55…" — so the phone is also
//    tried with that '1' dropped (same rule as normMxPhone). A query with no
//    digits never matches on phone.
//  - Mixed queries (letters + digits) try both sides; empty/space-only = no filter.
// Pure + exported so the matcher is unit-testable without rendering.
function foldText(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}
export function matchesThreadSearch(th, query) {
  const q = foldText(query);
  if (!q) return true;
  const name = foldText(th?.lead_name);
  if (name && name.includes(q)) return true;
  const qDigits = q.replace(/[^0-9]/g, '');
  if (!qDigits) return false;
  const phoneDigits = String(th?.lead_phone || '').replace(/[^0-9]/g, '');
  if (!phoneDigits) return false;
  if (phoneDigits.includes(qDigits)) return true;
  const deMobile = phoneDigits.replace(/^521(\d{10})$/, '52$1');
  return deMobile !== phoneDigits && deMobile.includes(qDigits);
}

function formatShortTime(tsMs) {
  if (!tsMs) return '';
  const n = typeof tsMs === 'string' ? parseInt(tsMs, 10) : tsMs;
  if (!n || Number.isNaN(n)) return '';
  const dt = new Date(n);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function WhatsAppInbox() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link one-shot guard: only act on a given ?phone/?dept key once, so the
  // URL-strip that follows doesn't re-fire selection (feat-calendar-ux-sep3).
  const deepLinkKeyRef = useRef(null);

  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(true);
  // selected identity = { leadPhone, department } (department part disambiguates
  // the ventas vs postventa conversation for the same number).
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState('replied'); // 'replied' | 'all'
  // Department filter: 'all' | 'ventas' | 'postventa'. Independent of the
  // replied/all tab so Respondidas|Todas keeps working WITHIN each department.
  const [deptFilter, setDeptFilter] = useState('all');
  // WA-INBOX-SEARCH-oct19: free-text name/phone filter, applied on top of the
  // department + replied/all filters (client-side over the loaded page).
  const [search, setSearch] = useState('');

  // ---- list ----
  const loadThreads = useCallback(async () => {
    try {
      // Request the most-recent 200 threads (backend caps hard at 500). This
      // keeps the list bounded/fast (single GSI query) without hiding active
      // conversations behind the backend's smaller default page size.
      const params =
        tab === 'replied' ? { replied: true, limit: 200 } : { limit: 200 };
      const res = await listWhatsappThreads(params);
      setThreads(res?.data?.threads || []);
    } catch (e) {
      // Silent on poll; only the first failure should toast.
      if (threadsLoading) {
        toast.error(t('whatsapp.errors.loadThreads'));
      }
    } finally {
      setThreadsLoading(false);
    }
  }, [t, threadsLoading, tab]);

  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, LIST_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Department + search filtered view. The backend already returns ventas +
  // postventa as separate rows (dedupe keyed by phone+department), so we only
  // filter here.
  const visibleThreads = useMemo(() => {
    const byDept =
      deptFilter === 'all'
        ? threads
        : threads.filter((th) => normalizeDept(th.department) === deptFilter);
    if (!search.trim()) return byDept;
    return byDept.filter((th) => matchesThreadSearch(th, search));
  }, [threads, deptFilter, search]);
  const searching = !!search.trim();

  const selectThread = useCallback((th) => {
    const key = threadKey(th);
    setSelected({ leadPhone: th.lead_phone, department: normalizeDept(th.department) });
    // Optimistically clear unread badge in the list for THIS thread only
    // (same phone's other-department thread keeps its own unread count).
    setThreads((prev) =>
      prev.map((row) =>
        threadKey(row) === key ? { ...row, unread_count: 0 } : row
      )
    );
  }, []);

  const selectedKey = selected
    ? `${selected.leadPhone || ''}|${normalizeDept(selected.department)}`
    : null;

  // Deep-link from the Calendar cita panel: /whatsapp?phone=<digits>&dept=<dept>
  // (feat-calendar-ux-sep3). "Ver conversación WhatsApp" must land the operator
  // ON the thread, not on the empty "Selecciona una conversación" state. Once
  // the list is loaded, find the thread matching the canonicalized phone AND
  // department, switch the department/replied filters if that thread is hidden
  // by the current view, select it, and scroll it into view. If no thread
  // matches -> toast "Conversación no encontrada" (never a silent dead-click).
  // One-shot per key; strips the params afterward so a refresh/re-click behaves.
  useEffect(() => {
    const phoneParam = (searchParams.get('phone') || '').trim();
    const deptParam = (searchParams.get('dept') || '').trim();
    if (!phoneParam) return;
    const key = `${phoneParam}|${deptParam}`;
    if (deepLinkKeyRef.current === key) return;
    // Wait until the thread list has loaded before matching (otherwise a real
    // thread would look "not found" during the initial fetch).
    if (threadsLoading) return;
    deepLinkKeyRef.current = key;

    const wantPhone = normMxPhone(phoneParam);
    const wantDept = deptParam ? normalizeDept(deptParam) : '';
    // Match on phone first; when a dept is supplied prefer the exact
    // phone+dept thread, but fall back to a phone-only match so a thread whose
    // department differs from the cita's still opens (better than a dead-click).
    const exact = threads.find(
      (th) =>
        normMxPhone(th.lead_phone) === wantPhone &&
        (!wantDept || normalizeDept(th.department) === wantDept)
    );
    const match =
      exact || threads.find((th) => normMxPhone(th.lead_phone) === wantPhone);

    if (match) {
      const matchDept = normalizeDept(match.department);
      // If the target thread is hidden by the current filters, switch them so it
      // becomes visible in the list: department filter must be 'all' or the
      // thread's dept, and the replied/all tab must be 'all' (a deep-linked
      // thread may not be in "Respondidas").
      setDeptFilter((prev) => (prev === 'all' || prev === matchDept ? prev : 'all'));
      setTab((prev) => (prev === 'all' ? prev : 'all'));
      // A stale search text must not hide the deep-linked thread.
      setSearch('');
      selectThread(match);
      // Scroll the selected row into view once React has painted it.
      const rowKey = threadKey(match);
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `[data-thread-key="${rowKey.replace(/"/g, '\\"')}"]`
        );
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest' });
        }
      });
    } else {
      toast.error(t('whatsapp.errors.threadNotFound'));
    }

    // Strip the deep-link params so the selection state is now user-owned and a
    // refresh/close doesn't re-fire.
    const next = new URLSearchParams(searchParams);
    next.delete('phone');
    next.delete('dept');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, threadsLoading, searchParams]);

  return (
    <div className="flex h-full min-h-0 gap-4">
      {/* Thread list */}
      <div className="w-80 flex-shrink-0 flex min-h-0 flex-col rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-emerald-500" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('whatsapp.title')}
          </h2>
        </div>
        {/* WA-INBOX-SEARCH-oct19: search by customer name or phone (partial). */}
        <div className="border-b border-gray-200 px-2 py-2 dark:border-gray-700">
          <label className="relative block">
            <span className="sr-only">{t('whatsapp.search.label')}</span>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            {/* WA-INBOX-SEARCH-CANCEL-oct20: type="text" + role="searchbox" (NOT
                type="search") — Chrome/Safari paint a native cancel × on
                <input type="search"> while it has a value, which doubled up
                with our custom ✕ button below. Escape clears like the native
                search box used to. */}
            <input
              type="text"
              role="searchbox"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && search) {
                  e.preventDefault();
                  setSearch('');
                }
              }}
              placeholder={t('whatsapp.search.placeholder')}
              aria-label={t('whatsapp.search.label')}
              data-testid="wa-search-input"
              autoComplete="off"
              className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-7 text-sm text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
            />
            {searching && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label={t('whatsapp.search.clear')}
                data-testid="wa-search-clear"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </label>
        </div>
        {/* Department filter: Todos | Ventas | Postventa */}
        <div className="flex gap-1 border-b border-gray-200 px-2 py-2 dark:border-gray-700">
          {['all', 'ventas', 'postventa'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setDeptFilter(key)}
              data-testid={`wa-deptfilter-${key}`}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                deptFilter === key
                  ? 'bg-emerald-500 text-white'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60'
              }`}
            >
              {t(`whatsapp.deptFilter.${key}`)}
            </button>
          ))}
        </div>
        {/* Replied / All filter toggle (within the selected department) */}
        <div className="flex gap-1 border-b border-gray-200 px-2 py-2 dark:border-gray-700">
          {['replied', 'all'].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
                tab === key
                  ? 'bg-emerald-500 text-white'
                  : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700/60'
              }`}
            >
              {t(`whatsapp.tabs.${key}`)}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {threadsLoading ? (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
              {t('common.loading')}
            </div>
          ) : visibleThreads.length === 0 ? (
            <div className="p-4 text-sm text-gray-500 dark:text-gray-400" data-testid="wa-threads-empty">
              {searching ? t('whatsapp.search.noResults') : t('whatsapp.empty.threads')}
            </div>
          ) : (
            visibleThreads.map((th) => {
              const key = threadKey(th);
              const dept = normalizeDept(th.department);
              return (
                <button
                  key={key}
                  type="button"
                  data-thread-key={key}
                  onClick={() => selectThread(th)}
                  className={`flex w-full flex-col gap-0.5 border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/50 ${
                    selectedKey === key ? 'bg-emerald-50 dark:bg-emerald-500/10' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    {/* Name left (bold), dept tag + phone right. */}
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {th.lead_name || th.lead_phone || t('whatsapp.noName')}
                    </span>
                    <span className="ml-2 flex flex-shrink-0 items-center gap-1.5">
                      <DeptTag department={dept} t={t} />
                      {th.lead_name && (
                        <span className="text-[11px] text-gray-400">
                          {th.lead_phone}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                      {th.opted_out ? (
                        <span className="inline-flex items-center gap-1 text-red-500">
                          <NoSymbolIcon className="h-3 w-3" />
                          {t('whatsapp.optedOut')}
                        </span>
                      ) : (
                        <>
                          <span className="mr-1 text-gray-400">
                            {formatShortTime(th.last_activity)}
                          </span>
                          {th.last_message || '—'}
                        </>
                      )}
                    </span>
                    {th.unread_count > 0 && (
                      <span className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-500 px-1.5 text-[11px] font-semibold text-white">
                        {th.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Thread view */}
      <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <WhatsAppThreadPanel
          leadPhone={selected?.leadPhone || null}
          department={selected?.department || ''}
        />
      </div>
    </div>
  );
}

export default WhatsAppInbox;
